import { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Check, X, RotateCw, ZoomIn, ZoomOut, Maximize } from 'lucide-react';

/**
 * Render the cropped area to a JPEG dataURL.
 *
 * `crop` is in the DISPLAYED image's percentage coordinate system (as
 * react-image-crop returns). We convert to natural pixel coordinates via
 * the image's naturalWidth/Height. Rotation is applied first (about the
 * image centre), then the crop rectangle is cut from the rotated bitmap.
 */
export async function getCroppedImg(src, crop, rotation = 0) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });

  const naturalW = img.naturalWidth;
  const naturalH = img.naturalHeight;

  // 1) rotate the whole image onto an offscreen canvas so the crop
  //    rectangle can be cut in the rotated frame.
  const rotRad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rotRad));
  const cos = Math.abs(Math.cos(rotRad));
  const rotW = naturalW * cos + naturalH * sin;
  const rotH = naturalW * sin + naturalH * cos;
  const rot = document.createElement('canvas');
  rot.width = rotW; rot.height = rotH;
  const rctx = rot.getContext('2d');
  rctx.translate(rotW / 2, rotH / 2);
  rctx.rotate(rotRad);
  rctx.drawImage(img, -naturalW / 2, -naturalH / 2);

  // 2) convert the % crop into rotated-pixel coords.
  const cropX = Math.round((crop.x / 100) * rotW);
  const cropY = Math.round((crop.y / 100) * rotH);
  const cropW = Math.round((crop.width  / 100) * rotW);
  const cropH = Math.round((crop.height / 100) * rotH);
  if (cropW <= 0 || cropH <= 0) {
    // Defensive — user somehow ended up with a zero-area crop. Fall back
    // to the full rotated image so we never return an empty PNG.
    return rot.toDataURL('image/jpeg', 0.9);
  }

  const out = document.createElement('canvas');
  out.width = cropW; out.height = cropH;
  out.getContext('2d').drawImage(rot, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
  return out.toDataURL('image/jpeg', 0.9);
}


/**
 * ImageCropper — freeform corner-draggable crop + rotate + zoom.
 *
 * Iter-41 rewrite: replaced react-easy-crop (fixed crop box, pan/zoom)
 * with react-image-crop (draggable corners + edges, free aspect). The
 * existing zoom + rotate controls stay; the crop rectangle now has a
 * handle at every corner AND every edge midpoint so a parent can carve
 * out a single page from an open-book photo where two pages sit side by
 * side.
 *
 * Props (unchanged from the previous implementation for a clean swap):
 *   src        — image data URL to crop
 *   onCancel() — user closed the modal without saving
 *   onCropped(dataUrl) — user confirmed a crop
 */
export default function ImageCropper({ src, onCancel, onCropped }) {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState(null);              // active drag (px in displayed frame)
  const [completedCrop, setCompletedCrop] = useState(null); // final crop (% in displayed frame)
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(false);

  /**
   * When the image finishes loading, initialise the crop rectangle to the
   * full image so the user can drag the corners inward from there — this
   * matches what a photo scanner UI does. We use react-image-crop's
   * `centerCrop` helper so the initial rect is exactly centred.
   */
  const onImgLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    // 96% of the frame keeps the corner handles visible against the black
    // background — 100% would clip them at the edges.
    const initial = centerCrop(
      makeAspectCrop({ unit: '%', width: 96 }, width / height, width, height),
      width, height,
    );
    // Adjust y so the box is centred vertically too.
    initial.y = (100 - initial.height) / 2;
    setCrop(initial);
    setCompletedCrop({ ...initial });
  }, []);

  const handleDone = async () => {
    if (!completedCrop || !imgRef.current) return;
    setBusy(true);
    try {
      const c = completedCrop.unit === '%'
        ? completedCrop
        : pxToPercent(completedCrop, imgRef.current);
      onCropped(await getCroppedImg(src, c, rotation));
    } finally { setBusy(false); }
  };

  const resetFull = () => {
    setCrop({ unit: '%', x: 2, y: 2, width: 96, height: 96 });
    setCompletedCrop({ unit: '%', x: 2, y: 2, width: 96, height: 96 });
  };

  return (
    <div data-testid="image-cropper" className="fixed inset-0 bg-black/90 z-[60] flex flex-col">
      <div className="flex justify-between items-center p-4 bg-black text-white">
        <button data-testid="crop-cancel-btn" onClick={onCancel} className="p-3 rounded-full bg-white/10">
          <X size={22} strokeWidth={3}/>
        </button>
        <div className="font-bold text-sm text-center flex-1 px-3">
          Drag the corners &amp; edges to fit exactly one page
        </div>
        <button data-testid="crop-done-btn" disabled={busy || !completedCrop}
          onClick={handleDone} className="p-3 rounded-full bg-[#FF6B1A] disabled:opacity-50">
          <Check size={22} strokeWidth={3}/>
        </button>
      </div>

      {/*
        Cropper stage. Overflow-auto so a zoomed image can be panned
        (react-image-crop scales its overlay with the img, so its handles
        stay aligned no matter the zoom). The wrapper is centred so a
        zoomed-out image doesn't sit in the top-left corner.
      */}
      <div className="relative flex-1 overflow-auto flex items-center justify-center p-3"
        data-testid="crop-stage">
        <ReactCrop
          crop={crop}
          onChange={(c, percent) => setCrop(percent)}
          onComplete={(c, percent) => setCompletedCrop(percent)}
          ruleOfThirds
          minWidth={30}
          minHeight={30}
          keepSelection
          data-testid="react-image-crop"
        >
          <img
            ref={imgRef}
            src={src}
            alt=""
            onLoad={onImgLoad}
            style={{
              transform: `rotate(${rotation}deg) scale(${zoom})`,
              transformOrigin: 'center center',
              maxWidth: '100%',
              maxHeight: '75vh',
              display: 'block',
            }}
          />
        </ReactCrop>
      </div>

      <div className="bg-black text-white p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <button data-testid="crop-zoom-out-btn" onClick={() => setZoom(z => Math.max(1, +(z - 0.1).toFixed(2)))}
            className="bg-white/10 hover:bg-white/20 p-2 rounded-full">
            <ZoomOut size={18} strokeWidth={2.6}/>
          </button>
          <input data-testid="crop-zoom-slider" type="range"
            min={1} max={3} step={0.05} value={zoom}
            onChange={e => setZoom(Number(e.target.value))}
            className="flex-1 min-w-[100px]"/>
          <button data-testid="crop-zoom-in-btn" onClick={() => setZoom(z => Math.min(3, +(z + 0.1).toFixed(2)))}
            className="bg-white/10 hover:bg-white/20 p-2 rounded-full">
            <ZoomIn size={18} strokeWidth={2.6}/>
          </button>
          <span className="text-xs opacity-70 w-10 text-right">{Math.round(zoom * 100)}%</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="crop-rotate-btn" onClick={() => setRotation(r => (r + 90) % 360)}
            className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-full inline-flex items-center gap-2">
            <RotateCw size={18} strokeWidth={3}/> Rotate
          </button>
          <button data-testid="crop-reset-btn" onClick={resetFull}
            className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-full inline-flex items-center gap-2">
            <Maximize size={18} strokeWidth={3}/> Reset crop
          </button>
          <span className="text-xs opacity-70 hidden sm:inline">Drag any corner or edge handle to fit exactly one page.</span>
        </div>
      </div>
    </div>
  );
}


// react-image-crop reports the completed crop in whichever unit was last
// set. Our getCroppedImg expects %; convert if we ever get pixels.
function pxToPercent(cropPx, imgEl) {
  const w = imgEl.width;   // displayed size
  const h = imgEl.height;
  return {
    unit: '%',
    x: (cropPx.x / w) * 100,
    y: (cropPx.y / h) * 100,
    width:  (cropPx.width  / w) * 100,
    height: (cropPx.height / h) * 100,
  };
}
